// Slot A — Receipt print bridge (WebUSB).
//
// Single source of truth for printer communication from the frontend.
// Used today: only from Settings ("Connect printer" + "Test print").
// Wired into POS auto-print in a later commit.
//
// Strategy:
//   1) Fetch ESC/POS bytes from GET /api/printing/receipt/{order_id}.
//   2) Send those bytes to the paired USB printer via WebUSB bulk-out.
//   3) Respect venue.settings.printing.enabled — when false, no-op.
//
// Auto-restore: on page load, navigator.usb.getDevices() returns any
// previously-granted device for this origin without re-prompting.
//
// No external deps. Vanilla browser WebUSB API.

import { api } from "@/lib/api";

// In-memory store, single bridge per tab.
let _device = null;
let _endpointOut = null;
let _interfaceNumber = null;
const _attempts = []; // newest first; capped at 10

function _now() {
  return new Date().toISOString();
}

function _logAttempt(kind, ok, detail) {
  _attempts.unshift({ at: _now(), kind, ok, detail: detail || "" });
  while (_attempts.length > 10) _attempts.pop();
}

function _supported() {
  return typeof navigator !== "undefined" && !!navigator.usb;
}

// iter_115 (2026-06-10) — Diagnostic snapshot for telemetry events.
//
// Returned alongside every bridge call (both success and failure) so the
// worker can include WHY a print succeeded/skipped/failed when it POSTs
// to /api/print/jobs/{id}/event. NEVER contains customer or payment
// data — only USB device + endpoint + claim state. Caller treats this
// as additive metadata; existing return shapes are NOT changed.
function _diag(extra) {
  const d = _device || null;
  const base = {
    webusb_supported: _supported(),
    paired: !!d,
    product_name: d?.productName || null,
    vendor_id: d?.vendorId ?? null,
    product_id: d?.productId ?? null,
    interface_number: _interfaceNumber,
    endpoint_out: _endpointOut,
    has_endpoint: _endpointOut != null,
  };
  if (extra && typeof extra === "object") {
    for (const k of Object.keys(extra)) {
      if (extra[k] !== undefined) base[k] = extra[k];
    }
  }
  return base;
}

// iter_104 (2026-06-08) P0 HOTFIX — Multi-device fan-out skipped contract.
//
// Since iter_101 mounted the print queue worker in Layout.jsx, EVERY
// authenticated browser tab (POS terminal, manager laptop, kitchen iPad,
// owner phone) polls /print/jobs/pending. The FIRST non-printer device
// to race the actual printer terminal will fail the WebUSB claim and —
// before this hotfix — POST /print/jobs/{id}/failed, flipping the row
// to status="failed" and removing it from the pending queue forever.
// The real printer terminal then sees an empty pending list and the
// docket / package label never reaches the kitchen.
//
// All "this device cannot print this job right now" classifications
// must therefore set skipped:true so the worker short-circuits without
// hitting /failed, leaving the job pending for the actual printer
// terminal. Only genuine mid-transfer errors AFTER a successful claim
// remain real failures.
const SKIPPABLE_CODES = new Set([
  "no_webusb",                       // browser lacks WebUSB
  "no_device_selected",              // no printer paired on this device
  "disconnected_replug_required",    // stale handle (sleep/unplug)
  "access_denied_os_claimed_or_busy",// OS or another tab holds it
  "no_endpoint",                     // selected device has no bulk-OUT
]);
function _isSkippable(code) {
  return SKIPPABLE_CODES.has(code);
}

async function _findBulkOut(device) {
  // Returns [interfaceNumber, endpointNumber] or [null, null].
  for (const cfg of device.configurations) {
    for (const iface of cfg.interfaces) {
      for (const alt of iface.alternates) {
        for (const ep of alt.endpoints) {
          if (ep.direction === "out" && ep.type === "bulk") {
            return [iface.interfaceNumber, ep.endpointNumber];
          }
        }
      }
    }
  }
  return [null, null];
}

async function _claim(device) {
  // Opens, selects configuration, finds and claims a bulk-OUT interface.
  // On failure, throws — caller is responsible for safe-release.
  if (!device.opened) {
    await device.open();
  }
  if (device.configuration === null) {
    await device.selectConfiguration(1);
  }
  const [iface, ep] = await _findBulkOut(device);
  if (iface == null || ep == null) {
    throw new Error("No bulk OUT endpoint found on printer");
  }
  await device.claimInterface(iface);
  _interfaceNumber = iface;
  _endpointOut = ep;
}

async function _safeRelease(device) {
  // Best-effort release of any handle. Never throws.
  try {
    if (device && _interfaceNumber != null && device.opened) {
      await device.releaseInterface(_interfaceNumber);
    }
  } catch (_) { /* ignore */ }
  try { if (device && device.opened) await device.close(); } catch (_) { /* ignore */ }
}

async function _release() {
  await _safeRelease(_device);
}

// Classify a WebUSB open()/claim() error into a stable code for the
// recent-attempts log and the UI. Returns one of:
//   "access_denied_os_claimed_or_busy" — ChromeOS/CUPS or another tab holds it
//   "disconnected_replug_required"    — device handle is stale (sleep/unplug)
//   "no_device_selected"              — picker cancelled or no granted device
//   "no_endpoint"                     — printer enumerates but has no bulk OUT
//   "<raw message>"                   — anything else, surfaced verbatim
function _classifyError(e) {
  const msg = (e?.message || String(e || "")).toLowerCase();
  if (msg.includes("access denied")) return "access_denied_os_claimed_or_busy";
  if (msg.includes("disconnected") ||
      msg.includes("not opened") ||
      msg.includes("not configured") ||
      msg.includes("transferout") ||
      msg.includes("the device has been lost") ||
      msg.includes("no device selected") && _device) {
    return "disconnected_replug_required";
  }
  if (msg.includes("no device selected") ||
      msg.includes("user cancelled") ||
      msg.includes("user canceled") ||
      msg.includes("permission denied by user")) {
    return "no_device_selected";
  }
  if (msg.includes("no bulk out")) return "no_endpoint";
  return e?.message || String(e || "unknown");
}

// Ensure we have a live, claimed bulk-OUT endpoint on a paired device.
// Tries the in-memory handle first; on stale/denied/disconnected, releases
// it and retries once with whatever Chrome reports as currently granted.
// Throws with `err.code` set to one of the codes above.
async function _ensureClaimed() {
  if (!_supported()) {
    const err = new Error("no_webusb");
    err.code = "no_webusb";
    throw err;
  }
  // Attempt 1 — use the in-memory handle if we have one.
  if (_device) {
    try {
      await _claim(_device);
      return _device;
    } catch (e1) {
      const code1 = _classifyError(e1);
      // The handle is stale or the OS is holding the device. Drop it.
      await _safeRelease(_device);
      _device = null;
      _interfaceNumber = null;
      _endpointOut = null;
      // If it was a user-cancel of a picker, propagate immediately —
      // don't retry from getDevices() in that case.
      if (code1 === "no_device_selected") {
        const err = new Error(code1);
        err.code = code1;
        throw err;
      }
      // Otherwise fall through to attempt 2.
    }
  }
  // Attempt 2 — re-read getDevices() and try once with the freshest grant.
  const devices = await navigator.usb.getDevices();
  const fresh = devices[0];
  if (!fresh) {
    const err = new Error("no_device_selected");
    err.code = "no_device_selected";
    throw err;
  }
  try {
    await _claim(fresh);
    _device = fresh;
    return fresh;
  } catch (e2) {
    const code2 = _classifyError(e2);
    await _safeRelease(fresh);
    _device = null;
    _interfaceNumber = null;
    _endpointOut = null;
    const err = new Error(code2);
    err.code = code2;
    throw err;
  }
}

function _b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ---------- public API ----------

const printBridge = {
  supported() {
    return _supported();
  },

  // Restore previously-granted USB device on page load. Does NOT throw
  // and does NOT touch in-memory state on failure; first real call to
  // _sendBytes / connect will recover via _ensureClaimed.
  async getDevices() {
    if (!_supported()) return [];
    try {
      const devices = await navigator.usb.getDevices();
      if (devices.length && !_device) {
        try {
          await _claim(devices[0]);
          _device = devices[0];
        } catch (e) {
          // OS may still be holding the device; that's recoverable later.
          await _safeRelease(devices[0]);
          _device = null;
          _interfaceNumber = null;
          _endpointOut = null;
          _logAttempt("restore", false, _classifyError(e));
        }
      }
      return devices.map((d) => ({
        productName: d.productName || "USB device",
        manufacturerName: d.manufacturerName || "",
        vendorId: d.vendorId,
        productId: d.productId,
      }));
    } catch (e) {
      _logAttempt("restore", false, _classifyError(e));
      return [];
    }
  },

  // Prompt the user to pick a USB printer. Robust against an existing
  // stale handle being held by the OS.
  async connect() {
    if (!_supported()) {
      const err = new Error("no_webusb");
      err.code = "no_webusb";
      throw err;
    }
    // Step 1 — release any in-memory handle first, before the picker runs.
    if (_device) {
      await _safeRelease(_device);
      _device = null;
      _interfaceNumber = null;
      _endpointOut = null;
    }
    let device;
    try {
      device = await navigator.usb.requestDevice({
        filters: [{ classCode: 7 }],
      });
    } catch (e) {
      const code = _classifyError(e);
      _logAttempt("connect", false, code);
      const err = new Error(code);
      err.code = code;
      throw err;
    }
    // Step 2 — try to claim. If denied/stale, retry once with the freshest
    // entry from getDevices() (Chrome may return a different USBDevice
    // instance after the picker).
    try {
      await _claim(device);
      _device = device;
      _logAttempt("connect", true, device.productName || "");
      return {
        ok: true,
        productName: device.productName || "USB device",
        vendorId: device.vendorId,
        productId: device.productId,
      };
    } catch (e1) {
      const code1 = _classifyError(e1);
      await _safeRelease(device);
      // Retry once with the freshest grant.
      try {
        const devices = await navigator.usb.getDevices();
        const fresh = devices[0];
        if (!fresh) throw new Error("no_device_selected");
        await _claim(fresh);
        _device = fresh;
        _logAttempt("connect", true, `${fresh.productName || ""} (retry)`);
        return {
          ok: true,
          productName: fresh.productName || "USB device",
          vendorId: fresh.vendorId,
          productId: fresh.productId,
        };
      } catch (e2) {
        const code2 = _classifyError(e2) || code1;
        _logAttempt("connect", false, code2);
        const err = new Error(code2);
        err.code = code2;
        throw err;
      }
    }
  },

  // Slot C stabilisation — release the in-memory handle and reset state.
  // Does NOT revoke the browser-level permission (only the user can do
  // that via chrome://settings/content/usbDevices). Safe to call any time.
  async reset() {
    await _safeRelease(_device);
    _device = null;
    _interfaceNumber = null;
    _endpointOut = null;
    _logAttempt("reset", true, "in-memory handle cleared");
    return { ok: true };
  },

  status() {
    return {
      supported: _supported(),
      paired: !!_device,
      productName: _device?.productName || null,
      lastAttempts: _attempts.slice(0, 5),
    };
  },

  // iter_115 (2026-06-10) — Expose the diagnostic snapshot so callers
  // (the print worker) can attach it to lifecycle events. Read-only
  // and safe to call any time; contains no customer/payment data.
  diag() {
    return _diag();
  },

  recentAttempts() {
    return _attempts.slice();
  },

  // Send raw ESC/POS bytes (Uint8Array) to the paired printer. Robust
  // against stale handles: _ensureClaimed will release+re-grab on the
  // first transferOut error path.
  async _sendBytes(bytes) {
    await _ensureClaimed();
    if (!_endpointOut) {
      const err = new Error("no_endpoint");
      err.code = "no_endpoint";
      throw err;
    }
    const chunk = 1024;
    try {
      for (let i = 0; i < bytes.length; i += chunk) {
        const slice = bytes.subarray(i, Math.min(i + chunk, bytes.length));
        await _device.transferOut(_endpointOut, slice);
      }
    } catch (e) {
      const code = _classifyError(e);
      // Best-effort one-shot recovery: drop the handle, re-ensure, retry.
      await _safeRelease(_device);
      _device = null;
      _interfaceNumber = null;
      _endpointOut = null;
      await _ensureClaimed();
      for (let i = 0; i < bytes.length; i += chunk) {
        const slice = bytes.subarray(i, Math.min(i + chunk, bytes.length));
        try {
          await _device.transferOut(_endpointOut, slice);
        } catch (e2) {
          const code2 = _classifyError(e2) || code;
          const err = new Error(code2);
          err.code = code2;
          throw err;
        }
      }
    }
  },

  // Send the receipt for `orderId`. Fetches bytes from the backend,
  // then delivers them via WebUSB. Honours venue kill switch.
  async printReceipt(orderId, opts) {
    return this._printDoc("receipt", orderId, opts);
  },

  // Send the KITCHEN docket for `orderId`. Same transport + kill switch
  // as receipts. Different endpoint, different attempt label.
  async printDocket(orderId, opts) {
    return this._printDoc("docket", orderId, opts);
  },

  // iter_117 (2026-06-10) — Full kitchen docket REPRINT from Orders
  // History. Mirrors printDocket exactly (same transport, killswitch,
  // skipped contract, diagnostics) but routes to the new backend path
  // `/printing/docket-full/{order_id}` which calls
  // build_docket(include_held_courses=True) so held mains/desserts
  // under meal firing ARE included. Create-time worker path and every
  // other bridge method are untouched.
  async printDocketFull(orderId, opts) {
    return this._printDoc("docket-full", orderId, opts);
  },

  // iter_96 (2026-06-10) — Per-station kitchen docket. Calls the
  // existing `/printing/docket/{order_id}` endpoint with ?station=<name>
  // so the backend filters items to that station only. Same transport
  // + skipped contract + diag as printDocket; just adds a query param.
  // Worker enqueues one job per distinct station when per-station
  // routing is enabled on the venue.
  async printDocketStation(orderId, station, opts) {
    const passthrough = opts || {};
    const params = { ...(passthrough.params || {}), station };
    return this._printDoc("docket", orderId, { ...passthrough, params });
  },

  // iter_102 (2026-06-07) — Customer package receipt / bag label.
  // Used for delivery + preorder orders. Same WebUSB transport as the
  // normal receipt; different backend endpoint (build_package_receipt).
  async printPackageReceipt(orderId, opts) {
    return this._printDoc("package-receipt", orderId, opts);
  },

  // iter_95 (2026-03-01) — Course-fire docket print (FIRE MAINS / FIRE DESSERTS).
  // Backend route: GET /api/printing/docket-course/{order_id}?course=main
  async printDocketCourse(orderId, course, opts) {
    const venueSettings = opts?.venueSettings || {};
    const enabled = venueSettings?.printing?.enabled !== false;
    if (!enabled) {
      _logAttempt("docket-course", false, "killswitch off");
      return { ok: false, skipped: true, reason: "killswitch",
               diag: _diag({ reason: "killswitch" }) };
    }
    if (!_supported()) {
      _logAttempt("docket-course", false, "no_webusb");
      return { ok: false, skipped: true, reason: "no_webusb",
               code: "no_webusb",
               diag: _diag({ reason: "no_webusb" }) };
    }
    try {
      const r = await api.get(
        `/printing/docket-course/${orderId}?course=${encodeURIComponent(course)}`);
      const bytes = _b64ToBytes(r.data.bytes_b64);
      await this._sendBytes(bytes);
      _logAttempt("docket-course", true,
        `ticket ${r.data.ticket_number ?? "—"} (${course})`);
      return { ok: true, fallbackText: r.data.fallback_text,
               ticketNumber: r.data.ticket_number,
               diag: _diag() };
    } catch (e) {
      const code = e?.code || _classifyError(e);
      _logAttempt("docket-course", false, e?.response?.data?.detail || code);
      // iter_104 — any code that means "this device cannot print this
      // job right now" must NOT mark the print_job as failed. The job
      // stays pending so the actual printer terminal can pick it up.
      const isSkippable = _isSkippable(code);
      return { ok: false, skipped: isSkippable || undefined,
               reason: isSkippable ? code : "error", code,
               error: e?.message || String(e),
               diag: _diag({ reason: code, error: e?.message || String(e) }) };
    }
  },

  // iter_86 — Replacement docket / receipt prints. Same transport, same
  // kill switch — different backend paths so the worker keeps a clear
  // audit trail of which prints were edit-driven.
  async printDocketUpdate(orderId, opts) {
    return this._printDoc("docket-update", orderId, opts);
  },
  async printReceiptUpdate(orderId, opts) {
    return this._printDoc("receipt-update", orderId, opts);
  },

  // iter_88 (BUG 2) — VOID / CANCELLED kitchen slip. Backend route
  // /api/printing/void-slip/{id}?kind=void|cancelled. Reuses the same
  // WebUSB transport + killswitch as _printDoc, but the path layout
  // differs (id is mid-path, kind is a query string) so we inline.
  async printVoidSlip(orderId, opts) {
    const kind = (opts && opts.kind) || "void";
    const venueSettings = opts?.venueSettings || {};
    const enabled = venueSettings?.printing?.enabled !== false;
    if (!enabled) {
      _logAttempt("void-slip", false, "killswitch off");
      return { ok: false, skipped: true, reason: "killswitch",
               diag: _diag({ reason: "killswitch" }) };
    }
    if (!_supported()) {
      _logAttempt("void-slip", false, "no_webusb");
      return { ok: false, skipped: true, reason: "no_webusb",
               code: "no_webusb",
               diag: _diag({ reason: "no_webusb" }) };
    }
    try {
      const r = await api.get(`/printing/void-slip/${orderId}?kind=${encodeURIComponent(kind)}`);
      const bytes = _b64ToBytes(r.data.bytes_b64);
      await this._sendBytes(bytes);
      _logAttempt("void-slip", true, `ticket ${r.data.ticket_number ?? "—"} (${kind})`);
      return { ok: true, fallbackText: r.data.fallback_text,
               ticketNumber: r.data.ticket_number,
               diag: _diag() };
    } catch (e) {
      const code = e?.code || _classifyError(e);
      _logAttempt("void-slip", false, e?.response?.data?.detail || code);
      // iter_104 — any "device cannot print right now" code returns
      // skipped:true so non-printer fan-out browsers don't kill the job.
      const isSkippable = _isSkippable(code);
      return { ok: false, skipped: isSkippable || undefined,
               reason: isSkippable ? code : "error", code,
               error: e?.message || String(e),
               diag: _diag({ reason: code, error: e?.message || String(e) }) };
    }
  },

  // 2026-02-22 — Refund/Void receipt print. Same WebUSB transport, same
  // kill switch, same fallback contract as receipt/docket. Different
  // verbs/paths:
  //   - POST /api/print/refund/{refund_id}
  //   - POST /api/print/void/{order_id}   (optional body.reason)
  // Returns the same `{ok, reason, skipped, fallbackText, ...}` shape so
  // the caller can reuse the existing toast/fallback pattern.
  async printRefund(refundId, opts) {
    return this._printSlip("refund", refundId, undefined, opts);
  },
  async printVoid(orderId, reason, opts) {
    return this._printSlip("void", orderId, reason, opts);
  },

  // POST-based variant of _printDoc. The only behavioural difference is
  // the HTTP verb + path prefix (`/print/...` vs `/printing/...`) and
  // that void carries an optional `{reason}` body. Everything else —
  // kill switch, WebUSB transport, recent-attempts log, error mapping —
  // is identical.
  async _printSlip(kind, id, reason, opts) {
    const venueSettings = opts?.venueSettings || {};
    const enabled = venueSettings?.printing?.enabled !== false;
    if (!enabled) {
      _logAttempt(kind, false, "killswitch off");
      return { ok: false, skipped: true, reason: "killswitch",
               diag: _diag({ reason: "killswitch" }) };
    }
    if (!_supported()) {
      _logAttempt(kind, false, "no_webusb");
      return { ok: false, skipped: true, reason: "no_webusb",
               code: "no_webusb",
               diag: _diag({ reason: "no_webusb" }) };
    }
    try {
      const body = kind === "void" && reason ? { reason } : {};
      const r = await api.post(`/print/${kind}/${id}`, body);
      const bytes = _b64ToBytes(r.data.bytes_b64);
      await this._sendBytes(bytes);
      _logAttempt(kind, true, `ticket ${r.data.ticket_number ?? "—"}`);
      return { ok: true, fallbackText: r.data.fallback_text,
               ticketNumber: r.data.ticket_number,
               reasonUsed: r.data.reason_used,
               diag: _diag() };
    } catch (e) {
      const code = e?.code || _classifyError(e);
      _logAttempt(kind, false, e?.response?.data?.detail || code);
      const reasonMap = {
        no_device_selected: "not_paired",
        access_denied_os_claimed_or_busy: "error",
        disconnected_replug_required: "error",
        no_endpoint: "error",
        no_webusb: "no_webusb",
      };
      // iter_104 — same skipped contract as _printDoc: any "device
      // cannot print right now" classification stays pending.
      const isSkippable = _isSkippable(code);
      return {
        ok: false,
        skipped: isSkippable || undefined,
        reason: reasonMap[code] || "error",
        code,
        error: e?.message || String(e),
        diag: _diag({ reason: code, error: e?.message || String(e) }),
      };
    }
  },

  async _printDoc(kind, orderId, opts) {
    const venueSettings = opts?.venueSettings || {};
    const enabled = venueSettings?.printing?.enabled !== false; // default ON
    if (!enabled) {
      _logAttempt(kind, false, "killswitch off");
      return { ok: false, skipped: true, reason: "killswitch",
               diag: _diag({ reason: "killswitch" }) };
    }
    if (!_supported()) {
      _logAttempt(kind, false, "no_webusb");
      return { ok: false, skipped: true, reason: "no_webusb",
               code: "no_webusb",
               diag: _diag({ reason: "no_webusb" }) };
    }
    try {
      // iter_96 (2026-06-10) — Optional query params (e.g. station filter)
      // pass through to the docket endpoint. Existing callers omit
      // `params` and behaviour is byte-identical.
      const r = await api.get(`/printing/${kind}/${orderId}`,
                              opts?.params ? { params: opts.params } : undefined);
      const bytes = _b64ToBytes(r.data.bytes_b64);
      await this._sendBytes(bytes);
      _logAttempt(kind, true, `ticket ${r.data.ticket_number ?? "—"}`);
      return { ok: true, fallbackText: r.data.fallback_text,
               ticketNumber: r.data.ticket_number,
               diag: _diag() };
    } catch (e) {
      const code = e?.code || _classifyError(e);
      _logAttempt(kind, false, e?.response?.data?.detail || code);
      // Map stable codes to legacy reason strings POS.jsx already branches on.
      const reasonMap = {
        no_device_selected: "not_paired",
        access_denied_os_claimed_or_busy: "error",
        disconnected_replug_required: "error",
        no_endpoint: "error",
        no_webusb: "no_webusb",
      };
      // iter_104 — any "device cannot print this job right now" code
      // (no_webusb, no_device_selected, disconnected_replug_required,
      // access_denied_os_claimed_or_busy, no_endpoint) MUST NOT mark
      // the print_job as failed. The job stays pending so the actual
      // printer terminal can pick it up. Only genuine mid-transfer
      // errors after a successful claim mark /failed.
      const isSkippable = _isSkippable(code);
      return {
        ok: false,
        skipped: isSkippable || undefined,
        reason: reasonMap[code] || "error",
        code,
        error: e?.message || String(e),
        diag: _diag({ reason: code, error: e?.message || String(e) }),
      };
    }
  },

  // Send a minimal probe receipt (no order id). Used by the
  // "Test print" button in Settings.
  async testPrint() {
    if (!_supported()) return { ok: false, reason: "no_webusb" };
    try {
      const enc = new TextEncoder();
      const sep = "=".repeat(48);
      const lines = [
        sep,
        "        MARGIN MATE PRINTER TEST",
        sep,
        `Time: ${new Date().toISOString().slice(0, 19).replace("T", " ")}`,
        "Status: OK",
        sep,
        "",
        "",
        "",
      ].join("\n");
      const init = new Uint8Array([0x1b, 0x40]); // ESC @
      const cut = new Uint8Array([0x1d, 0x56, 0x00]); // GS V 0
      const body = enc.encode(lines + "\n");
      const out = new Uint8Array(init.length + body.length + cut.length);
      out.set(init, 0);
      out.set(body, init.length);
      out.set(cut, init.length + body.length);
      await this._sendBytes(out);
      _logAttempt("test", true, "");
      return { ok: true };
    } catch (e) {
      const code = e?.code || _classifyError(e);
      _logAttempt("test", false, code);
      return { ok: false, code, error: e?.message || String(e), reason: code };
    }
  },

  // iter_129 (2026-06-13) — Per-station test print for the Print
  // Health Widget. Mirrors testPrint() transport/error-handling but
  // formats a station-labelled slip per the iter_129 spec:
  //   *** MARGIN MATE ONE ***
  //   *** TEST PRINT ***
  //
  //   Station: Coffee
  //   Time: 10:42am
  //
  //   If you can read this,
  //   the printer is working.
  // Strictly client-side — no backend coordination required.
  async testPrintStation(stationName) {
    if (!_supported()) return { ok: false, reason: "no_webusb" };
    const safeStation = (typeof stationName === "string" && stationName.trim())
      ? stationName.trim().slice(0, 40)
      : "Station";
    try {
      const enc = new TextEncoder();
      const now = new Date();
      // AU-style short time, lowercase am/pm, no leading zero.
      const timeStr = now.toLocaleTimeString("en-AU", {
        hour: "numeric", minute: "2-digit", hour12: true,
      }).replace(/\s/g, "").toLowerCase();
      const lines = [
        "*** MARGIN MATE ONE ***",
        "*** TEST PRINT ***",
        "",
        `Station: ${safeStation}`,
        `Time: ${timeStr}`,
        "",
        "If you can read this,",
        "the printer is working.",
        "",
        "",
        "",
      ].join("\n");
      const init = new Uint8Array([0x1b, 0x40]); // ESC @
      const cut = new Uint8Array([0x1d, 0x56, 0x00]); // GS V 0
      const body = enc.encode(lines + "\n");
      const out = new Uint8Array(init.length + body.length + cut.length);
      out.set(init, 0);
      out.set(body, init.length);
      out.set(cut, init.length + body.length);
      await this._sendBytes(out);
      _logAttempt("test-station", true, safeStation);
      return { ok: true, station: safeStation };
    } catch (e) {
      const code = e?.code || _classifyError(e);
      _logAttempt("test-station", false, `${safeStation}: ${code}`);
      return { ok: false, code, error: e?.message || String(e), reason: code };
    }
  },



  // Slot C — pulse the cash drawer via the existing paired printer.
  // Sends ESC @ (init) + ESC p 0x00 0x19 0xFA (drawer kick: pin 2,
  // 25 ms on, 250 ms off). Standard Epson-compatible pulse — what
  // MUNBYN documents for the ITPP098P RJ11 DK port.
  //
  // No toast on failure (call sites in Slot C Commits 2/3 are
  // fire-and-forget). The explicit Settings "Test drawer" button
  // surfaces success/failure toasts itself.
  async openDrawer(reason = "manual", opts) {
    const venueSettings = opts?.venueSettings || {};
    const enabled = venueSettings?.printing?.enabled !== false; // default ON
    if (!enabled) {
      _logAttempt("drawer", false, `killswitch off (${reason})`);
      return { ok: false, skipped: true, reason: "killswitch" };
    }
    if (!_supported()) {
      _logAttempt("drawer", false, `no_webusb (${reason})`);
      return { ok: false, reason: "no_webusb" };
    }
    try {
      // ESC @ then ESC p 0 25 250.
      const bytes = new Uint8Array([0x1b, 0x40, 0x1b, 0x70, 0x00, 0x19, 0xfa]);
      await this._sendBytes(bytes);
      _logAttempt("drawer", true, reason);
      return { ok: true };
    } catch (e) {
      const code = e?.code || _classifyError(e);
      _logAttempt("drawer", false, `${reason}: ${code}`);
      return { ok: false, reason: "error", code, error: e?.message || String(e) };
    }
  },
};

export default printBridge;
export { printBridge };
